import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Card, Typography, Button, TextField, Grid, Chip,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, Alert, Table, TableBody,
  TableCell, TableHead, TableRow, Paper, MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LoginIcon from "@mui/icons-material/Login";
import DeleteIcon from "@mui/icons-material/Delete";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import RefreshIcon from "@mui/icons-material/Refresh";
import api from "../../utils/api";

const EMPTY_FORM = {
  ClubMemberID:"", FirstName:"", LastName:"", Description:"",
  VehiclePlate:"", VehicleType:"", ExpectedArrival:"", ExpectedDeparture:"",
  Notes:"", AuthorizedBy:"", EnteredBy:"",
};

const GatePreAuthPage = () => {
  const [list, setList]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");
  const [qrOpen, setQrOpen]   = useState(false);
  const [qrUrl, setQrUrl]     = useState("");
  const [snack, setSnack]     = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/gate-preauth");
      setList(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Pre-fill EnteredBy from session
    try {
      const u = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "{}");
      setForm(f => ({ ...f, EnteredBy: u?.Username || u?.username || "" }));
    } catch {}
  }, [load]);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleAdd = async () => {
    if (!form.FirstName || !form.LastName) { setError("First and last name are required."); return; }
    setSaving(true); setError("");
    try {
      await api.post("/gate-preauth", form);
      setAddOpen(false);
      setForm(f => ({ ...EMPTY_FORM, EnteredBy: f.EnteredBy }));
      setSnack("Pre-authorization created.");
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create pre-authorization.");
    } finally {
      setSaving(false);
    }
  };

  const handleCheckIn = async (id) => {
    try {
      await api.post(`/gate-preauth/${id}/checkin`);
      setSnack("Visitor checked in successfully.");
      load();
    } catch (err) {
      alert(err.response?.data?.error || "Check-in failed.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this pre-authorization?")) return;
    try {
      await api.delete(`/gate-preauth/${id}`);
      load();
    } catch (err) {
      alert("Delete failed.");
    }
  };

  const handleShowQR = (id) => {
    setQrUrl(`${api.defaults.baseURL}/gate-preauth/${id}/qr`);
    setQrOpen(true);
  };

  const fmtDT = (d) => d ? new Date(d).toLocaleString() : "N/A";

  const cardSx = {
    borderRadius:"16px", border:"1px solid rgba(47,99,173,0.08)",
    boxShadow:"0 4px 16px rgba(0,0,0,0.06)", p:2.5,
  };
  const fieldSx = { "& .MuiOutlinedInput-root":{ borderRadius:"10px", backgroundColor:"rgba(255,255,255,0.96)" } };
  const btnSx = { borderRadius:"10px", textTransform:"none", fontWeight:700, boxShadow:"none",
    backgroundColor:"#2f63ad", "&:hover":{ backgroundColor:"#1f4f94", boxShadow:"none" } };

  return (
    <Box>
      {snack && <Alert severity="success" onClose={()=>setSnack("")} sx={{ mb:2, borderRadius:2 }}>{snack}</Alert>}

      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:3 }}>
        <Typography variant="h5" sx={{ fontWeight:800, color:"#223142" }}>Pre-Authorized Visitors</Typography>
        <Box sx={{ display:"flex", gap:1 }}>
          <Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} sx={btnSx} onClick={()=>{ setAddOpen(true); setError(""); }}>
            Pre-Authorize Visitor
          </Button>
        </Box>
      </Box>

      {loading ? <CircularProgress /> : (
        <Card sx={cardSx}>
          {list.length === 0 ? (
            <Typography variant="body1" sx={{ color:"#6b778c", textAlign:"center", py:4 }}>
              No pre-authorized visitors. Use the button above to add one.
            </Typography>
          ) : (
            <Paper elevation={0} sx={{ borderRadius:"10px", overflow:"hidden", border:"1px solid rgba(47,99,173,0.08)" }}>
              <Table size="small">
                <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}>
                  <TableRow>
                    {["Name","Member ID","Expected Arrival","Vehicle","Status","Actions"].map(h=>(
                      <TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.map((pa) => (
                    <TableRow key={pa.PreAuthID} hover>
                      <TableCell sx={{ fontWeight:600, color:"#223142" }}>{pa.FirstName} {pa.LastName}</TableCell>
                      <TableCell sx={{ color:"#6b778c", fontSize:12 }}>{pa.ClubMemberID || "N/A"}</TableCell>
                      <TableCell sx={{ color:"#6b778c", fontSize:12 }}>{fmtDT(pa.ExpectedArrival)}</TableCell>
                      <TableCell sx={{ color:"#6b778c", fontSize:12 }}>{pa.VehiclePlate || "N/A"}</TableCell>
                      <TableCell>
                        <Chip size="small" label={pa.IsCheckedIn ? "Checked In" : "Pending"}
                          sx={{ fontWeight:600, fontSize:11, borderRadius:"8px",
                            bgcolor: pa.IsCheckedIn ? "rgba(15,118,110,0.12)" : "rgba(180,83,9,0.10)",
                            color:   pa.IsCheckedIn ? "#0f766e" : "#b45309" }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display:"flex", gap:0.5 }}>
                          {!pa.IsCheckedIn && (
                            <Tooltip title="Check In Now">
                              <IconButton size="small" color="success" onClick={()=>handleCheckIn(pa.PreAuthID)}>
                                <LoginIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="View QR Code">
                            <IconButton size="small" color="primary" onClick={()=>handleShowQR(pa.PreAuthID)}>
                              <QrCode2Icon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={()=>handleDelete(pa.PreAuthID)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Card>
      )}

      {/* Add Dialog */}
      <Dialog open={addOpen} onClose={()=>setAddOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx:{ borderRadius:"16px" } }}>
        <DialogTitle sx={{ fontWeight:700, color:"#223142", pb:1 }}>Pre-Authorize Visitor</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb:2, borderRadius:2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt:0.5 }}>
            <Grid item xs={6}>
              <TextField name="FirstName" label="First Name *" fullWidth value={form.FirstName}
                onChange={handleChange} sx={fieldSx} size="small" />
            </Grid>
            <Grid item xs={6}>
              <TextField name="LastName" label="Last Name *" fullWidth value={form.LastName}
                onChange={handleChange} sx={fieldSx} size="small" />
            </Grid>
            <Grid item xs={12}>
              <TextField name="ClubMemberID" label="Member ID" fullWidth value={form.ClubMemberID}
                onChange={handleChange} sx={fieldSx} size="small" />
            </Grid>
            <Grid item xs={12}>
              <TextField name="Description" label="Description" fullWidth value={form.Description}
                onChange={handleChange} sx={fieldSx} size="small" />
            </Grid>
            <Grid item xs={6}>
              <TextField name="VehiclePlate" label="Vehicle Plate" fullWidth value={form.VehiclePlate}
                onChange={handleChange} sx={fieldSx} size="small" />
            </Grid>
            <Grid item xs={6}>
              <TextField name="VehicleType" label="Vehicle Type" fullWidth value={form.VehicleType}
                onChange={handleChange} sx={fieldSx} size="small" />
            </Grid>
            <Grid item xs={6}>
              <TextField name="ExpectedArrival" label="Expected Arrival" type="datetime-local"
                fullWidth value={form.ExpectedArrival} onChange={handleChange}
                InputLabelProps={{ shrink:true }} sx={fieldSx} size="small" />
            </Grid>
            <Grid item xs={6}>
              <TextField name="ExpectedDeparture" label="Expected Departure" type="datetime-local"
                fullWidth value={form.ExpectedDeparture} onChange={handleChange}
                InputLabelProps={{ shrink:true }} sx={fieldSx} size="small" />
            </Grid>
            <Grid item xs={12}>
              <TextField name="AuthorizedBy" label="Authorized By" fullWidth value={form.AuthorizedBy}
                onChange={handleChange} sx={fieldSx} size="small" />
            </Grid>
            <Grid item xs={12}>
              <TextField name="Notes" label="Notes" fullWidth multiline rows={2} value={form.Notes}
                onChange={handleChange} sx={fieldSx} size="small" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px:3, pb:3, gap:1 }}>
          <Button onClick={()=>setAddOpen(false)} variant="outlined"
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Cancel</Button>
          <Button onClick={handleAdd} variant="contained" disabled={saving}
            sx={{ ...btnSx }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : "Save Pre-Authorization"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrOpen} onClose={()=>setQrOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx:{ borderRadius:"16px" } }}>
        <DialogTitle sx={{ fontWeight:700, color:"#223142" }}>Visitor QR Code</DialogTitle>
        <DialogContent sx={{ textAlign:"center", pb:3 }}>
          <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
            Share this QR code with the visitor. Gate staff can scan it to check them in instantly.
          </Typography>
          {qrUrl && (
            <Box component="img" src={qrUrl} alt="QR Code"
              sx={{ width:200, height:200, border:"1px solid rgba(0,0,0,0.1)", borderRadius:"12px" }} />
          )}
        </DialogContent>
        <DialogActions sx={{ px:3, pb:3 }}>
          <Button onClick={()=>setQrOpen(false)} variant="outlined"
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Close</Button>
          <Button href={qrUrl} download="visitor-qr.png" variant="contained" sx={btnSx}>
            Download QR
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GatePreAuthPage;
